// Task #705 — Ads Manager Phase 1 schema definitions.
//
// Lives in its own file to keep the 10k-line shared/schema.ts manageable;
// re-exported from shared/schema.ts at the bottom so callers can keep
// importing from `@shared/schema` as usual.
import { sql } from "drizzle-orm";
import { z } from "zod";
import { createInsertSchema } from "drizzle-zod";
import {
  pgTable, varchar, text, timestamp, jsonb, integer, bigint, date, index,
} from "drizzle-orm/pg-core";

// ---- Ad accounts ---------------------------------------------------------
export const adAccounts = pgTable("ad_accounts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  platform: varchar("platform", { length: 20 }).notNull(),           // 'google' | 'meta'
  externalAccountId: varchar("external_account_id", { length: 128 }).notNull(),
  displayName: varchar("display_name", { length: 255 }),
  currencyCode: varchar("currency_code", { length: 8 }),
  status: varchar("status", { length: 20 }).notNull().default("active"),
  // OAuth (envelope-encrypted via workspace KEK).
  accessTokenEnc: text("access_token_enc"),
  refreshTokenEnc: text("refresh_token_enc"),
  accessTokenExpiresAt: timestamp("access_token_expires_at"),
  scopes: text("scopes"),
  // Conversions API.
  conversionsApiTokenEnc: text("conversions_api_token_enc"),
  conversionActionsJson: jsonb("conversion_actions_json").notNull().default(sql`'{}'::jsonb`),
  metadata: jsonb("metadata").notNull().default(sql`'{}'::jsonb`),
  lastSyncedAt: timestamp("last_synced_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  wsIdx: index("ad_accounts_ws_idx").on(t.workspaceId, t.platform),
}));
export type AdAccount = typeof adAccounts.$inferSelect;
export const insertAdAccountSchema = createInsertSchema(adAccounts).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertAdAccount = z.infer<typeof insertAdAccountSchema>;

// ---- Campaigns -----------------------------------------------------------
export const adCampaigns = pgTable("ad_campaigns", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  adAccountId: varchar("ad_account_id").notNull(),
  platform: varchar("platform", { length: 20 }).notNull(),
  externalCampaignId: varchar("external_campaign_id", { length: 128 }),
  name: varchar("name", { length: 255 }).notNull(),
  objective: varchar("objective", { length: 64 }),
  status: varchar("status", { length: 20 }).notNull().default("draft"),
  dailyBudgetCents: bigint("daily_budget_cents", { mode: "number" }),
  lifetimeBudgetCents: bigint("lifetime_budget_cents", { mode: "number" }),
  startAt: timestamp("start_at"),
  endAt: timestamp("end_at"),
  utmDefaultSource: varchar("utm_default_source", { length: 64 }),
  utmDefaultMedium: varchar("utm_default_medium", { length: 64 }),
  utmDefaultCampaign: varchar("utm_default_campaign", { length: 128 }),
  metadata: jsonb("metadata").notNull().default(sql`'{}'::jsonb`),
  lastSyncedAt: timestamp("last_synced_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  wsIdx: index("ad_campaigns_ws_idx").on(t.workspaceId, t.platform, t.status),
  accountIdx: index("ad_campaigns_account_idx").on(t.adAccountId),
  externalIdx: index("ad_campaigns_external_idx").on(t.workspaceId, t.platform, t.externalCampaignId),
}));
export type AdCampaign = typeof adCampaigns.$inferSelect;
export const insertAdCampaignSchema = createInsertSchema(adCampaigns).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertAdCampaign = z.infer<typeof insertAdCampaignSchema>;

// ---- Ad sets -------------------------------------------------------------
export const adSets = pgTable("ad_sets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  campaignId: varchar("campaign_id").notNull(),
  externalAdSetId: varchar("external_ad_set_id", { length: 128 }),
  name: varchar("name", { length: 255 }).notNull(),
  status: varchar("status", { length: 20 }).notNull().default("draft"),
  targeting: jsonb("targeting").notNull().default(sql`'{}'::jsonb`),
  dailyBudgetCents: bigint("daily_budget_cents", { mode: "number" }),
  metadata: jsonb("metadata").notNull().default(sql`'{}'::jsonb`),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  campaignIdx: index("ad_sets_campaign_idx").on(t.campaignId),
  wsIdx: index("ad_sets_ws_idx").on(t.workspaceId),
}));
export type AdSet = typeof adSets.$inferSelect;
export const insertAdSetSchema = createInsertSchema(adSets).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertAdSet = z.infer<typeof insertAdSetSchema>;

// ---- Creatives -----------------------------------------------------------
export const adCreatives = pgTable("ad_creatives", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  campaignId: varchar("campaign_id"),
  kind: varchar("kind", { length: 20 }).notNull().default("image"),
  headline: varchar("headline", { length: 255 }),
  body: text("body"),
  callToAction: varchar("call_to_action", { length: 64 }),
  imageObjectPath: varchar("image_object_path", { length: 1024 }),
  imageUrl: varchar("image_url", { length: 1024 }),
  aiPrompt: text("ai_prompt"),
  aiModel: varchar("ai_model", { length: 64 }),
  generatedBy: varchar("generated_by"),
  metadata: jsonb("metadata").notNull().default(sql`'{}'::jsonb`),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  wsIdx: index("ad_creatives_ws_idx").on(t.workspaceId),
  campaignIdx: index("ad_creatives_campaign_idx").on(t.campaignId),
}));
export type AdCreative = typeof adCreatives.$inferSelect;
export const insertAdCreativeSchema = createInsertSchema(adCreatives).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertAdCreative = z.infer<typeof insertAdCreativeSchema>;

// ---- Ads -----------------------------------------------------------------
export const ads = pgTable("ads", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  campaignId: varchar("campaign_id").notNull(),
  adSetId: varchar("ad_set_id"),
  creativeId: varchar("creative_id"),
  externalAdId: varchar("external_ad_id", { length: 128 }),
  name: varchar("name", { length: 255 }).notNull(),
  status: varchar("status", { length: 20 }).notNull().default("draft"),
  destinationUrl: varchar("destination_url", { length: 1024 }),
  metadata: jsonb("metadata").notNull().default(sql`'{}'::jsonb`),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  campaignIdx: index("ads_campaign_idx").on(t.campaignId),
  wsIdx: index("ads_ws_idx").on(t.workspaceId),
}));
export type Ad = typeof ads.$inferSelect;
export const insertAdSchema = createInsertSchema(ads).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertAd = z.infer<typeof insertAdSchema>;

// ---- Daily metric rollup -------------------------------------------------
export const adMetricsDaily = pgTable("ad_metrics_daily", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  adAccountId: varchar("ad_account_id").notNull(),
  campaignId: varchar("campaign_id"),
  adId: varchar("ad_id"),
  metricDate: date("metric_date").notNull(),
  impressions: bigint("impressions", { mode: "number" }).notNull().default(0),
  clicks: bigint("clicks", { mode: "number" }).notNull().default(0),
  spendCents: bigint("spend_cents", { mode: "number" }).notNull().default(0),
  conversions: bigint("conversions", { mode: "number" }).notNull().default(0),
  conversionValueCents: bigint("conversion_value_cents", { mode: "number" }).notNull().default(0),
  raw: jsonb("raw").notNull().default(sql`'{}'::jsonb`),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  wsDateIdx: index("ad_metrics_daily_ws_date_idx").on(t.workspaceId, t.metricDate),
}));
export type AdMetricDaily = typeof adMetricsDaily.$inferSelect;
export const insertAdMetricDailySchema = createInsertSchema(adMetricsDaily).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertAdMetricDaily = z.infer<typeof insertAdMetricDailySchema>;

// ---- Tracking phone numbers ---------------------------------------------
export const campaignPhoneNumbers = pgTable("campaign_phone_numbers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  campaignId: varchar("campaign_id"),
  twilioNumberSid: varchar("twilio_number_sid", { length: 64 }),
  phoneNumber: varchar("phone_number", { length: 32 }).notNull(),
  forwardTo: varchar("forward_to", { length: 32 }),
  status: varchar("status", { length: 20 }).notNull().default("active"),
  metadata: jsonb("metadata").notNull().default(sql`'{}'::jsonb`),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
}, (t) => ({
  campaignIdx: index("campaign_phone_numbers_campaign_idx").on(t.campaignId),
}));
export type CampaignPhoneNumber = typeof campaignPhoneNumbers.$inferSelect;
export const insertCampaignPhoneNumberSchema = createInsertSchema(campaignPhoneNumbers).omit({
  id: true, createdAt: true, updatedAt: true,
});
export type InsertCampaignPhoneNumber = z.infer<typeof insertCampaignPhoneNumberSchema>;

// ---- Attribution touches (raw click log) --------------------------------
export const attributionTouches = pgTable("attribution_touches", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  visitorId: varchar("visitor_id", { length: 64 }),
  gclid: varchar("gclid", { length: 255 }),
  fbclid: varchar("fbclid", { length: 255 }),
  // Task #706 — Phase 2 click ids: TikTok (`ttclid`) + LinkedIn (`li_fat_id`).
  ttclid: varchar("ttclid", { length: 255 }),
  liFatId: varchar("li_fat_id", { length: 255 }),
  utmSource: varchar("utm_source", { length: 128 }),
  utmMedium: varchar("utm_medium", { length: 128 }),
  utmCampaign: varchar("utm_campaign", { length: 255 }),
  utmTerm: varchar("utm_term", { length: 255 }),
  utmContent: varchar("utm_content", { length: 255 }),
  landingUrl: varchar("landing_url", { length: 2048 }),
  referrer: varchar("referrer", { length: 2048 }),
  userAgent: varchar("user_agent", { length: 512 }),
  ipHash: varchar("ip_hash", { length: 64 }),
  matchedCampaignId: varchar("matched_campaign_id"),
  creditedLeadId: varchar("credited_lead_id"),
  creditedClientId: varchar("credited_client_id"),
  creditedInvoiceId: varchar("credited_invoice_id"),
  occurredAt: timestamp("occurred_at").notNull().defaultNow(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (t) => ({
  visitorIdx: index("attribution_touches_visitor_idx").on(t.workspaceId, t.visitorId),
  gclidIdx: index("attribution_touches_gclid_idx").on(t.workspaceId, t.gclid),
  fbclidIdx: index("attribution_touches_fbclid_idx").on(t.workspaceId, t.fbclid),
  ttclidIdx: index("attribution_touches_ttclid_idx").on(t.workspaceId, t.ttclid),
  liFatIdIdx: index("attribution_touches_li_fat_id_idx").on(t.workspaceId, t.liFatId),
  leadIdx: index("attribution_touches_lead_idx").on(t.creditedLeadId),
  invoiceIdx: index("attribution_touches_invoice_idx").on(t.creditedInvoiceId),
}));
export type AttributionTouch = typeof attributionTouches.$inferSelect;
export const insertAttributionTouchSchema = createInsertSchema(attributionTouches).omit({
  id: true, createdAt: true,
});
export type InsertAttributionTouch = z.infer<typeof insertAttributionTouchSchema>;

// ---- Conversion pings (Conversions API audit log) -----------------------
export const conversionPings = pgTable("conversion_pings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  platform: varchar("platform", { length: 20 }).notNull(),
  eventName: varchar("event_name", { length: 64 }).notNull(),
  sourceKind: varchar("source_kind", { length: 20 }),
  sourceId: varchar("source_id"),
  adAccountId: varchar("ad_account_id"),
  campaignId: varchar("campaign_id"),
  gclid: varchar("gclid", { length: 255 }),
  fbclid: varchar("fbclid", { length: 255 }),
  // Task #706 — Phase 2 click ids for TikTok Events API + LinkedIn CAPI.
  ttclid: varchar("ttclid", { length: 255 }),
  liFatId: varchar("li_fat_id", { length: 255 }),
  conversionValueCents: bigint("conversion_value_cents", { mode: "number" }),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  attempts: integer("attempts").notNull().default(0),
  lastError: text("last_error"),
  requestPayload: jsonb("request_payload"),
  responsePayload: jsonb("response_payload"),
  enqueuedAt: timestamp("enqueued_at").notNull().defaultNow(),
  sentAt: timestamp("sent_at"),
}, (t) => ({
  wsStatusIdx: index("conversion_pings_ws_status_idx").on(t.workspaceId, t.status),
}));
export type ConversionPing = typeof conversionPings.$inferSelect;
export const insertConversionPingSchema = createInsertSchema(conversionPings).omit({
  id: true, enqueuedAt: true, sentAt: true,
});
export type InsertConversionPing = z.infer<typeof insertConversionPingSchema>;

// ---- Shared zod shape for attribution capture (used by routes) ----------
export const attributionInputSchema = z.object({
  visitorId: z.string().max(64).optional().nullable(),
  gclid: z.string().max(255).optional().nullable(),
  fbclid: z.string().max(255).optional().nullable(),
  // Task #706 — Phase 2 click ids.
  ttclid: z.string().max(255).optional().nullable(),
  liFatId: z.string().max(255).optional().nullable(),
  utmSource: z.string().max(128).optional().nullable(),
  utmMedium: z.string().max(128).optional().nullable(),
  utmCampaign: z.string().max(255).optional().nullable(),
  utmTerm: z.string().max(255).optional().nullable(),
  utmContent: z.string().max(255).optional().nullable(),
  landingUrl: z.string().max(2048).optional().nullable(),
}).partial();
export type AttributionInput = z.infer<typeof attributionInputSchema>;
